import { format } from "date-fns";
import { RacePlan } from "./dategrid";
import { getWeekDistance, render, renderDist } from "./rendering";
import { Units } from "types/app";

/**
 * Converts a training plan to Notion-compatible Markdown format
 * This creates a structured document with headers, tables, and checkboxes
 * that can be copied directly into Notion
 */
export function toNotion(plan: RacePlan, units: Units): string | undefined {
  const lines: string[] = [];

  // Title
  lines.push(`# ${plan.raceType} Training Plan\n`);
  lines.push(
    `**Race Date:** ${format(plan.planDates.planEndDate, "MMMM d, yyyy")}\n`,
  );
  lines.push(`**Units:** ${units}\n`);
  lines.push(`---\n`);

  const weeks = plan.dateGrid.weeks;

  for (let i = 0; i < weeks.length; i++) {
    const currWeek = weeks[i];
    const distance = getWeekDistance(currWeek, units);

    // Week header
    if (i === weeks.length - 1) {
      lines.push(`## 🏁 Final Training Week!\n`);
    } else {
      lines.push(`## Week ${i + 1}\n`);
    }

    // Week summary
    if (distance[0] > 0) {
      lines.push(`**Total Distance:** ${renderDist(distance, units, units)}\n`);
    }
    lines.push(
      `**Dates:** ${format(currWeek.days[0].date, "MMM d")} - ${format(currWeek.days[6].date, "MMM d, yyyy")}\n`,
    );
    lines.push(``); // blank line

    // Daily workouts as a list with checkboxes
    for (let j = 0; j < currWeek.days.length; j++) {
      const currWorkout = currWeek.days[j];
      const dayName = format(currWorkout.date, "EEEE");
      const dateStr = format(currWorkout.date, "MMM d");

      if (currWorkout.event) {
        const [title, desc] = render(
          currWorkout.event,
          plan.sourceUnits,
          units,
        );

        // Create a todo checkbox for each workout
        lines.push(`- [ ] **${dayName}, ${dateStr}** — ${title}`);

        // Add description as indented text if it exists
        if (desc && desc.trim() !== "") {
          const descLines = desc
            .split("\n")
            .filter((line) => line.trim() !== "");
          descLines.forEach((descLine) => {
            lines.push(`    - ${descLine.trim()}`);
          });
        }
      } else {
        // Rest day
        lines.push(`- [ ] **${dayName}, ${dateStr}** — Rest`);
      }
    }

    lines.push(``); // blank line between weeks
    lines.push(`---\n`); // separator
  }

  // Add footer with summary
  lines.push(`## 📊 Plan Summary\n`);
  lines.push(`- **Total Weeks:** ${weeks.length}`);

  // Calculate total distance
  let totalDistance = 0;
  weeks.forEach((week) => {
    const distance = getWeekDistance(week, units);
    totalDistance += distance[0];
  });
  lines.push(`- **Total Distance:** ${Math.round(totalDistance)} ${units}`);
  lines.push(
    `- **Race Day:** ${format(plan.planDates.planEndDate, "EEEE, MMMM d, yyyy")}`,
  );

  return lines.join("\n");
}
